// React Router configuration and main App component

import { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router';
import Toaster from './components/Toast/Toaster';
import ErrorBoundary from './components/ErrorBoundary';
import { LoadingAnnouncer, LoadingFallback } from './components/Spinner';
import { useRouteAnnouncement } from './hooks/useRouteAnnouncement';
import RequireSession from './components/RequireSession';
import { useSessionStore } from './stores/sessionStore';

// Lazy load route components for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const ComparisonViewPage = lazy(() => import('./pages/ComparisonViewPage'));

const CreateSessionPage = lazy(() => import('./pages/CreateSessionPage'));
const JoinSessionPage = lazy(() => import('./pages/JoinSessionPage'));
const SessionLobbyPage = lazy(() => import('./pages/SessionLobbyPage'));
const SelectionPage = lazy(() => import('./pages/SelectionPage'));
const ResultsPage = lazy(() => import('./pages/ResultsPage'));
const GroupOrderPage = lazy(() => import('./pages/GroupOrderPage'));

const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const CookSetupPage = lazy(() => import('./pages/CookSetupPage'));
const WatchSetupPage = lazy(() => import('./pages/WatchSetupPage'));
const ShoppingListPage = lazy(() => import('./pages/ShoppingListPage'));
const CookViewPage = lazy(() => import('./pages/CookViewPage'));

// Routes wrapper - provides smooth page transitions
function AnimatedRoutes() {
  const location = useLocation();
  const pageRef = useRef<HTMLDivElement>(null);
  useRouteAnnouncement(pageRef);

  return (
    <div key={location.pathname} ref={pageRef} className="animate-slide-up">
      <Routes location={location}>
        {/* Home with auth */}
        <Route path="/" element={<HomePage />} />

        {/* Cook setup: the Craving and Headcount behind the fork's Cook card */}
        <Route path="/cook" element={<CookSetupPage />} />

        {/* Watch setup: the Mood behind the fork's Watch card (#369) */}
        <Route path="/watch" element={<WatchSetupPage />} />

        {/* Standalone delivery price comparison */}
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/compare/:placeId" element={<ComparisonViewPage />} />

        {/* Create new session */}
        <Route path="/create" element={<CreateSessionPage />} />

        {/* Join existing session */}
        <Route path="/join" element={<JoinSessionPage />} />

        {/* Every Session screen, behind the one guard that sends a URL with no
            matching Session in the store to Join with the code prefilled (#403) */}
        <Route path="/session/:sessionCode" element={<RequireSession />}>
          {/* Session lobby (waiting room) */}
          <Route index element={<SessionLobbyPage />} />

          {/* Selection screen */}
          <Route path="select" element={<SelectionPage />} />

          {/* Results screen */}
          <Route path="results" element={<ResultsPage />} />

          {/* Group Order screen */}
          <Route path="order" element={<GroupOrderPage />} />
        </Route>

        {/* The Shopping List: its own URL, outliving the Session that minted
            it. Deliberately not nested under /session — holding the link is
            the whole capability, and no Session need still exist. */}
        <Route path="/list/:listId" element={<ShoppingListPage />} />

        {/* The cook view: the same list, read at the stove. On the list URL on
            purpose — it inherits the capability and the 7-day clock, and
            reopening the link is the whole re-cook affordance (#251). */}
        <Route path="/list/:listId/cook" element={<CookViewPage />} />

        {/* Friends page */}
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* 404 - render Home in place; the unknown URL stays in the bar */}
        <Route path="*" element={<HomePage />} />
      </Routes>
    </div>
  );
}

function App() {
  // #351: unsubscribe the auth listener on unmount. `active` matters — under
  // StrictMode the cleanup runs before the dynamic import resolves, so a
  // subscription that lands afterwards must be dropped here, not kept.
  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    void import('./stores/authStore')
      .then(({ useAuthStore }) => useAuthStore.getState().initialize())
      .then((subscription) => {
        if (active) unsubscribe = () => subscription?.unsubscribe();
        else subscription?.unsubscribe();
      })
      .catch((error) => console.error('Failed to initialize auth:', error));
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!useSessionStore.getState().sessionCode) return;
    let active = true;
    let disconnect: (() => void) | undefined;
    void import('./services/socketBindings')
      .then(({ disconnectSocket, initializeSocket }) => {
        if (!active) return;
        initializeSocket();
        disconnect = disconnectSocket;
      })
      .catch((error) => console.error('Failed to initialize socket:', error));
    return () => {
      active = false;
      disconnect?.();
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        {/* Above the router on purpose: it must outlive every spinner that
              publishes into it, or the text arrives with the region. */}
        <LoadingAnnouncer />
        <Suspense fallback={<LoadingFallback />}>
          <AnimatedRoutes />
        </Suspense>
        <Toaster />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
